import {
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface SortableColumn<T> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  sortValue: (row: T) => number | string;
  render: (row: T) => ReactNode;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * A client-sorted, client-paged table.
 *
 * For lists that arrive whole rather than a page at a time — a category
 * breakdown, a tender-type split, every POS lane on the platform. At those
 * sizes paging on the client costs nothing and sorting needs no round trip.
 *
 * Lifted out of the dashboard when the POS lane list wanted the same
 * behaviour; the only thing that had to change was its wording, which came
 * from the `dashboard` namespace and would have read oddly anywhere else.
 */
export function SortableTable<T>({
  rows,
  columns,
  rowKey,
  loading,
  busy,
  pagination,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyMessage,
  initialSort,
  onRowClick,
  rowClickable,
}: {
  rows: T[];
  columns: SortableColumn<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Rows per page when paging on the client. */
  pageSize?: number;
  /** Overrides the generic "no data" line. */
  emptyMessage?: string;
  /** Defaults to the first column, descending. */
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  /** Rows are on screen and being refreshed. */
  busy?: boolean;
  /**
   * Makes each row activate something — a drill-down, typically.
   *
   * The whole row becomes a button rather than gaining a link in one cell, so
   * the target is the size of the row on a phone. Sorting still belongs to the
   * header buttons, which sit outside the row and are unaffected.
   */
  onRowClick?: (row: T) => void;
  /**
   * Which rows `onRowClick` applies to. Defaults to all of them.
   *
   * A row can be real data and still have nowhere to go — an unnamed grouping
   * cannot be filtered on — and such a row must not look pressable.
   */
  rowClickable?: (row: T) => boolean;
  /**
   * Server-side paging. `rows` is then one page rather than the whole set, so
   * the client paging is skipped and sorting only reorders the page on screen.
   */
  pagination?: {
    page: number;
    size: number;
    total: number;
    onPageChange: (page: number) => void;
  };
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState(initialSort?.key ?? columns[0]?.key);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    initialSort?.direction ?? 'desc'
  );
  const [page, setPage] = useState(0);

  // A grouping/tender-type switch swaps the whole row set out from under the
  // table; staying on page 3 of the old set would just show an empty page.
  useEffect(() => setPage(0), [rows]);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === sortKey);
    if (!column) return rows;
    return [...rows].sort((a, b) => {
      const av = column.sortValue(a);
      const bv = column.sortValue(b);
      const cmp =
        typeof av === 'string'
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, columns, sortKey, sortDir]);

  const pageRows = pagination
    ? sorted
    : sorted.slice(page * pageSize, (page + 1) * pageSize);

  function toggleSort(key: string) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  if (loading) {
    return (
      <div>
        {/* Shaped like the rows it stands in for — a card per row on a phone,
            a table row above that — so the wait reads as the list arriving
            rather than as an empty box. */}
        <div className="space-y-2 sm:hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2 rounded-xl border p-3">
              <Skeleton className="h-4 w-2/3" />
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {columns.slice(1).map((column) => (
                  <div key={column.key} className="space-y-1">
                    <Skeleton className="h-2.5 w-12" />
                    <Skeleton className="h-3.5 w-16" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="hidden divide-y rounded-xl border sm:block">
          {Array.from({ length: 5 }).map((_, row) => (
            <div key={row} className="flex items-center gap-4 px-4 py-3">
              {columns.map((column, index) => (
                <Skeleton
                  key={column.key}
                  className={cn('h-4', index === 0 ? 'flex-1' : 'w-20')}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    // A refetch can turn "no rows" into rows, so the empty state reports it too.
    return (
      <div className="relative rounded-xl">
        <BusyOverlay show={busy} />
        <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
          {emptyMessage ?? t('table.noData')}
        </p>
      </div>
    );
  }

  const sortColumn = columns.find((c) => c.key === sortKey);

  const activates = (row: T) =>
    Boolean(onRowClick) && (rowClickable?.(row) ?? true);

  /**
   * Row activation, as a set of props rather than a wrapper element: a table
   * row cannot hold a `<button>` around its cells, and a phone card holds a
   * `<dl>` that must not be nested inside one either.
   */
  const rowProps = (
    row: T
  ): {
    role?: 'button';
    tabIndex?: number;
    onClick?: () => void;
    onKeyDown?: (event: KeyboardEvent) => void;
    className?: string;
  } =>
    activates(row)
      ? {
          role: 'button' as const,
          tabIndex: 0,
          onClick: () => onRowClick?.(row),
          onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onRowClick?.(row);
            }
          },
          className:
            'hover:bg-muted/50 focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none',
        }
      : {};

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 sm:hidden">
        <Select value={sortKey} onValueChange={setSortKey}>
          <SelectTrigger className="w-full" aria-label={t('table.sortBy')}>
            <SelectValue>
              {t('table.sortBy')}: {sortColumn?.header}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {columns.map((column) => (
              <SelectItem key={column.key} value={column.key}>
                {column.header}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          aria-label={t(sortDir === 'asc' ? 'table.sortAsc' : 'table.sortDesc')}
          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
        >
          {sortDir === 'asc' ? (
            <ArrowUp className="size-4" />
          ) : (
            <ArrowDown className="size-4" />
          )}
        </Button>
      </div>

      <div className="relative sm:hidden">
        <BusyOverlay show={busy} />
        <div className={cn('space-y-2', busy && 'opacity-60 transition-opacity')}>
          {pageRows.map((row) => {
            const [first, ...rest] = columns;
            const { className, ...activation } = rowProps(row);
            return (
              <div
                key={rowKey(row)}
                {...activation}
                className={cn('space-y-2 rounded-xl border p-3', className)}
              >
                <p className="text-sm font-medium">{first.render(row)}</p>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                  {rest.map((column) => (
                    <div key={column.key} className="min-w-0">
                      <dt className="text-muted-foreground truncate text-xs">
                        {column.header}
                      </dt>
                      <dd className="text-sm">{column.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </div>
      </div>

      <div className="hidden rounded-xl border sm:block">
        <Table busy={busy}>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.align === 'right' ? 'text-right' : ''}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className={cn(
                      'hover:text-foreground inline-flex items-center gap-1',
                      column.align === 'right' && 'flex-row-reverse'
                    )}
                  >
                    {column.header}
                    {sortKey === column.key ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3" />
                      ) : (
                        <ArrowDown className="size-3" />
                      )
                    ) : (
                      <ChevronsUpDown className="size-3 opacity-40" />
                    )}
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((row) => (
              <TableRow key={rowKey(row)} {...rowProps(row)}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={column.align === 'right' ? 'text-right' : ''}
                  >
                    {column.render(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {pagination
        ? pagination.total > pagination.size && (
            <Pagination
              page={pagination.page}
              size={pagination.size}
              total={pagination.total}
              onPageChange={pagination.onPageChange}
            />
          )
        : sorted.length > pageSize && (
            <Pagination
              page={page}
              size={pageSize}
              total={sorted.length}
              onPageChange={setPage}
            />
          )}
    </div>
  );
}
